'use client';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  Splitter,
  Text,
  IconButton,
  Badge,
  BadgeHttp,
  Input,
  Button,
  Tooltip,
} from '@terra/ui-design-system';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  FolderLibraryIcon,
  Add01Icon,
  ReplayIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useReplaySessionStore } from '../../store/replaySessionStore';
import { useProjectStore, useTargetId } from '../../store/projectStore';
import { SimpleReplayTab } from './SimpleReplayTab';
import { EmptyState } from '../EmptyState';
import {
  extractConnectionTarget,
  formatRequestToText,
} from '../../utils/httpFormatter';
import { ReplaySessionGroups } from '../replay/ReplaySessionGroups';
import { CollapsedPanelStrip } from '../layout/CollapsedPanelStrip';
import { LayoutSplitterResizeTrigger } from '../layout/LayoutSplitterResizeTrigger';
import { useReplayCollectionsLayout } from '../../hooks/layout/useReplayCollectionsLayout';

export function ReplayTabWrapper() {
  const tSessions = useTranslations('replay.sessions');
  const tCollections = useTranslations('replay.collections');
  const collectionsLayout = useReplayCollectionsLayout();
  const {
    sessions: allSessions,
    activeSessionId,
    setActiveSession,
    removeSession,
    updateSessionTitle,
    addSession,
  } = useReplaySessionStore();
  const targetId = useTargetId();
  const applicationUrl = useProjectStore((state) => state.selectedTarget?.url);
  const activeCollectionId = useReplaySessionStore(
    (state) => state.activeCollectionId,
  );
  const createEmptySession = useCallback(() => {
    let baseUrl = applicationUrl || 'https://example.com';

    try {
      const parsed = new URL(baseUrl);

      baseUrl = `${parsed.protocol}//${parsed.host}`;
    } catch {
      baseUrl = 'https://example.com';
    }

    const defaultUrl = baseUrl;
    const host = new URL(defaultUrl).host;
    const defaultRequest = {
      method: 'GET',
      url: defaultUrl,
      headers: { Host: host },
      body: '',
    };

    addSession({
      title: '/',
      applicationId: targetId,
      request: defaultRequest,
      responseHistory: [],
      currentResponseIndex: -1,
      requestData: formatRequestToText(defaultRequest),
      connectionTarget: extractConnectionTarget(defaultUrl),
      activeCredential: null,
    });
  }, [addSession, targetId, applicationUrl]);
  const sessionsInApplication = useMemo(() => {
    if (!targetId) {
      return allSessions;
    }

    return allSessions.filter((session) => session.applicationId === targetId);
  }, [allSessions, targetId]);
  const sessions = useMemo(() => {
    return sessionsInApplication.filter(
      (session) => session.collectionId === activeCollectionId,
    );
  }, [sessionsInApplication, activeCollectionId]);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const isSessionsLineCollapsed = useReplaySessionStore(
    (state) => state.isSessionsLineCollapsed,
  );
  const setSessionsLineCollapsed = useReplaySessionStore(
    (state) => state.setSessionsLineCollapsed,
  );

  useEffect(() => {
    if (sessions.length === 0) {
      return;
    }

    const activeSessionExists = activeSessionId
      ? sessions.some((session) => session.id === activeSessionId)
      : false;

    if (!activeSessionExists) {
      setActiveSession(sessions[0].id);
    }
  }, [activeSessionId, sessions, setActiveSession]);
  const activeSession =
    sessions.find((session) => session.id === activeSessionId) ?? sessions[0];
  const handleStartEdit = (sessionId: string, currentTitle: string) => {
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  const handleSaveEdit = () => {
    if (editingSessionId && editingTitle.trim()) {
      updateSessionTitle(editingSessionId, editingTitle.trim());
    }

    setEditingSessionId(null);
    setEditingTitle('');
  };

  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleMiddleClick = (e: React.MouseEvent, sessionId: string) => {
    if (e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      removeSession(sessionId);
    }
  };

  if (sessionsInApplication.length === 0) {
    return (
      <EmptyState
        icon={ReplayIcon}
        title={tSessions('emptyStateTitle')}
        description={tSessions('emptyStateDescription')}
        action={
          <Button size="xs" variant="outline" onClick={createEmptySession}>
            <Add01Icon size={14} />
            {tSessions('newSession')}
          </Button>
        }
      />
    );
  }

  return (
    <Splitter.Root
      ref={collectionsLayout.splitterRef}
      key={collectionsLayout.panelKey}
      orientation="horizontal"
      h="full"
      w="full"
      panels={collectionsLayout.panels}
      size={collectionsLayout.controlledSizes}
      onResize={collectionsLayout.handleResize}
      onResizeEnd={collectionsLayout.handleResizeEnd}
      borderWidth="0"
      borderRadius="0"
      gap={0}
    >
      <Splitter.Panel
        id="collections"
        borderWidth="0"
        borderRadius="0"
        overflow="hidden"
        className={collectionsLayout.collapsedClassName}
      >
        {collectionsLayout.isCollapsed ? (
          <CollapsedPanelStrip
            position="left"
            title={tCollections('title')}
            icon={FolderLibraryIcon}
            onClick={collectionsLayout.toggle}
          />
        ) : (
          <ReplaySessionGroups
            sessions={sessionsInApplication}
            activeSessionId={activeSessionId}
            onSelectSession={setActiveSession}
            onCloseSession={removeSession}
            onRenameSession={updateSessionTitle}
            onMinimize={collectionsLayout.toggle}
          />
        )}
      </Splitter.Panel>

      <LayoutSplitterResizeTrigger id="collections:main" />

      <Splitter.Panel
        id="main"
        borderWidth="0"
        borderRadius="0"
        w="full"
        minW="0"
      >
        <Box h="full" w="full" minW="0" display="flex" flexDirection="column">
          {sessions.length > 0 ? (
            <>
              <HStack
                gap="0"
                borderBottomWidth="1px"
                borderColor="border.subtle"
                bg="bg.subtle"
                flexShrink={0}
                minH="8"
                alignItems="flex-start"
              >
                <HStack
                  gap="0"
                  flex="1"
                  minW="0"
                  overflowX={isSessionsLineCollapsed ? 'auto' : 'hidden'}
                  overflowY={isSessionsLineCollapsed ? 'hidden' : 'visible'}
                  flexWrap={isSessionsLineCollapsed ? 'nowrap' : 'wrap'}
                  alignItems="center"
                  css={{
                    ...(isSessionsLineCollapsed
                      ? {
                          scrollbarWidth: 'none',
                          '&::-webkit-scrollbar': { display: 'none' },
                        }
                      : {}),
                  }}
                >
                  {sessions.map((session) => {
                    const isEditing = editingSessionId === session.id;

                    return (
                      <Tooltip
                        key={session.id}
                        title={`${session.request.method} ${session.title}`}
                        disabled={isEditing}
                        openDelay={300}
                      >
                        <HStack
                          gap="1"
                          px="2"
                          py="1"
                          borderRightWidth="1px"
                          borderColor="border.subtle"
                          bg={
                            activeSessionId === session.id
                              ? 'bg.default'
                              : 'transparent'
                          }
                          borderBottomWidth="2px"
                          borderBottomColor={
                            activeSessionId === session.id
                              ? 'border.accent'
                              : 'transparent'
                          }
                          cursor="pointer"
                          onClick={() =>
                            !isEditing && setActiveSession(session.id)
                          }
                          onMouseDown={(e) => handleMiddleClick(e, session.id)}
                          position="relative"
                          minW="60px"
                          maxW="250px"
                          h="8"
                          _hover={{
                            bg:
                              activeSessionId === session.id
                                ? 'bg.default'
                                : 'bg.muted',
                          }}
                        >
                          <BadgeHttp
                            method={session.request.method}
                            size="sm"
                            flexShrink={0}
                          >
                            {session.request.method}
                          </BadgeHttp>
                          {isEditing ? (
                            <Input
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={handleKeyDown}
                              onBlur={handleSaveEdit}
                              size="xs"
                              flex="1"
                              minW="0"
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                              fontSize="xs"
                              h="auto"
                              py="0"
                              px="1"
                            />
                          ) : (
                            <Text
                              fontSize="xs"
                              truncate
                              flex="1"
                              minW="0"
                              color={
                                activeSessionId === session.id
                                  ? 'fg.default'
                                  : 'fg.muted'
                              }
                              fontWeight={
                                activeSessionId === session.id
                                  ? 'medium'
                                  : 'normal'
                              }
                              cursor="text"
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                handleStartEdit(session.id, session.title);
                              }}
                            >
                              {session.title}
                            </Text>
                          )}
                          {session.responseHistory.length > 0 && !isEditing && (
                            <Badge size="sm" variant="emphasis" flexShrink={0}>
                              {session.responseHistory.length}
                            </Badge>
                          )}
                          {isEditing ? (
                            <IconButton
                              variant="link"
                              size="xs"
                              aria-label="Save title"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveEdit();
                              }}
                              color="fg.muted"
                              _hover={{
                                color: 'fg.default',
                                bg: 'transparent',
                              }}
                              minW="auto"
                              h="auto"
                              p="1"
                              flexShrink={0}
                            >
                              <Tick01Icon size={10} />
                            </IconButton>
                          ) : (
                            <IconButton
                              variant="link"
                              size="xs"
                              aria-label="Close session"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSession(session.id);
                              }}
                              color="fg.muted"
                              _hover={{
                                color: 'fg.default',
                                bg: 'transparent',
                              }}
                              minW="auto"
                              h="auto"
                              p="1"
                              flexShrink={0}
                            >
                              <Cancel01Icon size={10} />
                            </IconButton>
                          )}
                        </HStack>
                      </Tooltip>
                    );
                  })}
                </HStack>
                <HStack
                  pl="2"
                  pr="2"
                  gap="1"
                  borderLeftWidth="1px"
                  borderLeftColor="border.default"
                  flexShrink={0}
                  position="sticky"
                  right="0"
                  bg="bg.subtle"
                  h="8"
                >
                  <Tooltip title={tSessions('createReplaySession')}>
                    <IconButton
                      variant="link"
                      size="sm"
                      aria-label={tSessions('createReplaySession')}
                      color="fg.muted"
                      minW="9"
                      h="9"
                      px="2"
                      _hover={{
                        color: 'fg.default',
                        bg: 'bg.muted',
                      }}
                      onClick={createEmptySession}
                    >
                      <Add01Icon size={15} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      isSessionsLineCollapsed
                        ? tSessions('expandSessionsLine')
                        : tSessions('collapseSessionsLine')
                    }
                    disabled={sessions.length <= 1}
                  >
                    <IconButton
                      variant="link"
                      size="sm"
                      aria-label={
                        isSessionsLineCollapsed
                          ? tSessions('expandSessionsLine')
                          : tSessions('collapseSessionsLine')
                      }
                      color="fg.muted"
                      minW="9"
                      h="9"
                      px="2"
                      disabled={sessions.length <= 1}
                      _hover={{
                        color: 'fg.default',
                        bg: 'bg.muted',
                      }}
                      onClick={() =>
                        setSessionsLineCollapsed(!isSessionsLineCollapsed)
                      }
                    >
                      {isSessionsLineCollapsed ? (
                        <ArrowDown01Icon size={15} />
                      ) : (
                        <ArrowUp01Icon size={15} />
                      )}
                    </IconButton>
                  </Tooltip>
                </HStack>
              </HStack>

              {activeSession && (
                <Box flex="1" w="full" minW="0" overflow="hidden">
                  <SimpleReplayTab sessionId={activeSession.id} />
                </Box>
              )}
            </>
          ) : (
            <EmptyState
              icon={ReplayIcon}
              title={tSessions('emptyStateTitle')}
              description={tSessions('emptyCollectionDescription')}
              action={
                <Button
                  size="xs"
                  variant="outline"
                  onClick={createEmptySession}
                >
                  <Add01Icon size={14} />
                  {tSessions('newSession')}
                </Button>
              }
            />
          )}
        </Box>
      </Splitter.Panel>
    </Splitter.Root>
  );
}
